/**
 * 触屏机的全局状态（点餐流程的中枢）。
 *
 * 为什么用模块级 reactive 而不是 Pinia：
 * 和后台一样，这里只有「一台机器、一位顾客、一张单」这一份状态，
 * 多引一个状态库不划算。
 *
 * 一条贯穿始终的原则：**前端不自己算钱**。
 * 购物车栏上的合计只是过渡显示（用的是 shared 里那份被测试覆盖过的整数分函数），
 * 真正给顾客看、给顾客付的金额，一律来自服务端的试算接口。
 */
import { computed, reactive } from 'vue';
import type { CartItem } from '../cart/cart';
import {
  addItem as addCartItem,
  changeQuantity,
  removeByProductIds,
  removeItem,
  setRemark,
  subtotalCents,
  toOrderItems,
  totalQuantity,
} from '../cart/cart';
import { ApiError, createApiClient } from '../api/client';
import { createEndpoints, type Endpoints, type OrderItemPayload } from '../api/endpoints';
import type {
  CreatedOrder,
  MenuCategory,
  PayIntent,
  PaymentConfig,
  PreviewResult,
  TableMenuResponse,
} from '../api/types';
import { ensureDeviceId, resolveKioskConfig, type KioskConfig } from '../config';
import { createSession, nextAttemptKey, type KioskSession } from '../session/session';

/** 顾客看得见的提示 */
export interface Notice {
  type: 'error' | 'info';
  text: string;
}

/** 读环境变量（浏览器里就是 import.meta.env） */
function readEnv(): Record<string, string | undefined> {
  return (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
}

/** 取本机存储（隐私模式下访问会抛错，所以包一层） */
function localStorageSafe(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

interface KioskState {
  config: KioskConfig;
  endpoints: Endpoints;
  /** 后端当前的支付能力（真实/模拟），由后端说了算 */
  paymentConfig: PaymentConfig | null;
  menu: MenuCategory[];
  /** 桌台模式下的桌号（一体机模式为 null） */
  table: TableMenuResponse['table'] | null;
  menuLoading: boolean;
  menuError: string | null;
  cart: CartItem[];
  session: KioskSession;
  /** 当前这一单 */
  order: CreatedOrder | null;
  /** 服务端试算结果 */
  preview: PreviewResult | null;
  /** 支付二维码 */
  payIntent: PayIntent | null;
  notice: Notice | null;
  busy: boolean;
  /** 后端连不上了 —— 界面要明确提示，而不是干等着 */
  offline: boolean;
}

/** 取当前地址栏的查询串（非浏览器环境返回空） */
function currentSearch(): string {
  try {
    return typeof window === 'undefined' ? '' : window.location.search;
  } catch {
    return '';
  }
}

const client = createApiClient(resolveKioskConfig(readEnv(), 'bootstrap').apiBase);
const config = resolveKioskConfig(readEnv(), ensureDeviceId(localStorageSafe()), currentSearch());

const state = reactive<KioskState>({
  config,
  endpoints: createEndpoints(client),
  paymentConfig: null,
  menu: [],
  table: null,
  menuLoading: false,
  menuError: null,
  cart: [],
  session: createSession(config.deviceId),
  order: null,
  preview: null,
  payIntent: null,
  notice: null,
  busy: false,
  offline: false,
});

/**
 * 「我正在给谁点餐」—— 两种模式唯一的实质差别。
 *
 * 一体机：storeCode + 顾客标识（设备号派生），没有桌号
 * 扫桌码：qrToken（令牌里就带着是哪一桌），不用 storeCode
 *
 * 后端本来就支持这两种，所以前端只需要在这里分一次。
 */
export function orderScope(config: KioskConfig): { storeCode?: string; qrToken?: string } {
  if (config.mode === 'table' && config.tableToken) {
    return { qrToken: config.tableToken };
  }
  return { storeCode: config.storeCode };
}

/** 给页面用的只读视图 */
export const kiosk = {
  state,
  /** 购物车合计（过渡显示，最终以服务端试算为准） */
  cartSubtotalCents: computed(() => subtotalCents(state.cart)),
  cartCount: computed(() => totalQuantity(state.cart)),
  /** 当前是不是模拟收款（后端没回应时按「模拟」展示，宁可保守） */
  isSimulated: computed(() => state.paymentConfig?.isSimulated ?? true),

  /**
   * 查/付这一单用的凭据。
   *
   * 为什么不能一律用 openId：扫桌码下的单，后端记的凭据是 qrToken，
   * 拿 openId 去查会 404（而且是对的设计 —— 凭据不匹配就不该看得到别人的单）。
   */
  credential(): { openId?: string; qrToken?: string } {
    if (state.config.mode === 'table' && state.config.tableToken) {
      return { qrToken: state.config.tableToken };
    }
    return { openId: state.session.customerOpenId };
  },

  async init(): Promise<void> {
    await Promise.all([this.loadPaymentConfig(), this.loadMenu()]);
  },

  /** 读后端的支付能力：界面据此如实告诉顾客「现在是不是真收钱」 */
  async loadPaymentConfig(): Promise<void> {
    try {
      state.paymentConfig = await state.endpoints.getPaymentConfig();
      state.offline = false;
    } catch {
      // 读不到就当作模拟模式，绝不假装在收真钱
      state.paymentConfig = null;
    }
  },

  async loadMenu(): Promise<void> {
    state.menuLoading = true;
    state.menuError = null;
    try {
      if (state.config.mode === 'table' && state.config.tableToken) {
        // 扫桌码进来：菜单和桌号一起拿，页面才能显示「你在 A01 桌」
        const data = await state.endpoints.getTableMenu(state.config.tableToken);
        state.menu = data.categories;
        state.table = data.table;
      } else {
        const data = await state.endpoints.getMenu(state.config.storeCode);
        state.menu = data.categories;
        state.table = null;
      }
      state.offline = false;
    } catch (error) {
      const apiError = error as ApiError;
      state.menuError = apiError?.message ?? '菜单加载不出来，请叫服务员';
      state.offline = apiError?.isNetworkError === true;
    } finally {
      state.menuLoading = false;
    }
  },

  /** 开始一次新的顾客会话（点「开始点餐」时调用） */
  startSession(): void {
    state.session = createSession(state.config.deviceId);
    state.cart = [];
    state.preview = null;
    state.order = null;
    state.payIntent = null;
    state.notice = null;
  },

  /** 清空并回到「等待下一位顾客」的状态 */
  resetToIdle(): void {
    this.startSession();
  },

  setNotice(notice: Notice | null): void {
    state.notice = notice;
  },

  addToCart(input: Parameters<typeof addCartItem>[1]): void {
    try {
      state.cart = addCartItem(state.cart, input);
    } catch (error) {
      state.notice = { type: 'error', text: (error as Error).message };
    }
  },

  changeQuantity(key: string, delta: number): void {
    state.cart = changeQuantity(state.cart, key, delta);
  },

  removeItem(key: string): void {
    state.cart = removeItem(state.cart, key);
  },

  setRemark(key: string, remark: string): void {
    state.cart = setRemark(state.cart, key, remark);
  },

  /** 购物车里的项转成接口格式 */
  orderItems(): OrderItemPayload[] {
    return toOrderItems(state.cart);
  },

  /**
   * 试算（服务端算钱）。
   * 顾客点「去结算」时调一次；确认页显示的金额完全以它为准。
   */
  async previewOrder(): Promise<boolean> {
    if (state.cart.length === 0) {
      state.notice = { type: 'error', text: '还没有点菜呢' };
      return false;
    }
    state.busy = true;
    try {
      state.preview = await state.endpoints.previewOrder(orderScope(state.config), this.orderItems());
      // 试算会把「哪一项不能下单」逐条告诉我们，这时就把它们从购物车摘掉，
      // 让顾客立刻看到结果，而不是等提交时才失败
      if (state.preview.issues.length > 0) {
        const badIds = state.preview.issues.map((issue) => issue.productId);
        state.cart = removeByProductIds(state.cart, badIds);
        state.notice = {
          type: 'error',
          text: state.preview.issues.map((issue) => issue.message).join('；'),
        };
      }
      state.offline = false;
      return true;
    } catch (error) {
      const apiError = error as ApiError;
      state.notice = { type: 'error', text: apiError?.message ?? '算钱没成功，请重试' };
      state.offline = apiError?.isNetworkError === true;
      return false;
    } finally {
      state.busy = false;
    }
  },

  /**
   * 提交订单。
   *
   * @returns 成功返回订单；菜单变了（售罄/改价）返回 'menu-changed'；其他失败返回 null
   */
  async submitOrder(): Promise<CreatedOrder | 'menu-changed' | null> {
    if (state.cart.length === 0) {
      state.notice = { type: 'error', text: '还没有点菜呢' };
      return null;
    }
    state.busy = true;
    try {
      const order = await state.endpoints.submitOrder({
        ...orderScope(state.config),
        // 扫桌码进来的在语义上就是「顾客用自己手机点单」，和微信小程序同一条渠道；
        // 一体机才是 KIOSK。这样报表里能分清两种入口各做了多少单。
        channel: state.config.mode === 'table' ? 'MINI_PROGRAM' : 'KIOSK',
        customerOpenId: state.session.customerOpenId,
        // 幂等键：顾客连点两下「确认下单」只会产生一张单
        idempotencyKey: state.session.idempotencyKey,
        items: this.orderItems(),
      });
      state.order = order;
      state.offline = false;
      return order;
    } catch (error) {
      const apiError = error as ApiError;
      state.offline = apiError?.isNetworkError === true;

      if (apiError?.needsMenuRefresh) {
        // 菜单变了：刷新菜单 → 重新试算拿到「到底哪几项有问题」→ 摘掉它们 → 换幂等键
        await this.loadMenu();
        try {
          const preview = await state.endpoints.previewOrder(orderScope(state.config), this.orderItems());
          state.preview = preview;
          const badIds = preview.issues.map((issue) => issue.productId);
          if (badIds.length > 0) state.cart = removeByProductIds(state.cart, badIds);
          state.notice = {
            type: 'error',
            text: preview.issues.length > 0
              ? preview.issues.map((issue) => issue.message).join('；')
              : apiError.message,
          };
        } catch {
          state.notice = { type: 'error', text: apiError.message };
        }
        // 内容变了就必须换幂等键，否则会拿回上一张旧单
        nextAttemptKey(state.session, state.config.deviceId);
        return 'menu-changed';
      }

      state.notice = { type: 'error', text: apiError?.message ?? '下单没成功，请叫服务员' };
      return null;
    } finally {
      state.busy = false;
    }
  },

  /** 发起扫码支付（出二维码） */
  async startPay(): Promise<boolean> {
    if (!state.order) {
      state.notice = { type: 'error', text: '还没有下单' };
      return false;
    }
    state.busy = true;
    try {
      const intent = await state.endpoints.createPayIntent(state.order.orderNo, this.credential());
      state.payIntent = intent;
      state.offline = false;
      return true;
    } catch (error) {
      const apiError = error as ApiError;
      state.offline = apiError?.isNetworkError === true;
      state.notice = { type: 'error', text: apiError?.message ?? '二维码没出来，请叫服务员' };
      return false;
    } finally {
      state.busy = false;
    }
  },

  /** 取最新支付状态（支付页轮询靠它；返回 null 表示这次没问到，按「还没付」处理） */
  async fetchPayStatus() {
    if (!state.order) return null;
    try {
      const status = await state.endpoints.getPayStatus(state.order.orderNo, this.credential());
      state.offline = false;
      return status;
    } catch (error) {
      state.offline = (error as ApiError)?.isNetworkError === true;
      return null;
    }
  },

  /**
   * 模拟通道「确认收款」。
   * ⚠️ 只有在「部署方显式允许（VITE_KIOSK_ALLOW_TEST_PAY=true）」并且
   *    「后端确实处于模拟模式」时才显示入口。真实模式下调它必然 501。
   */
  async confirmTestPay(): Promise<boolean> {
    if (!state.order) return false;
    try {
      await state.endpoints.confirmTestPayment(state.order.orderNo, this.credential());
      return true;
    } catch (error) {
      state.notice = { type: 'error', text: (error as ApiError)?.message ?? '确认没成功' };
      return false;
    }
  },
};

export type KioskStore = typeof kiosk;
